import React, { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Radio, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

// How much motion we keep before the shutter (a fresh segment is restarted
// while idle so the clip never grows unbounded) and after the shutter.
const IDLE_SEGMENT_MS = 1800;
// Keep the motion clip small so it uploads fast on a phone. The still photo
// is a full-resolution canvas snapshot, so a low video bitrate does not hurt
// the picture quality at all.
const VIDEO_BITS = 900_000;
const AUDIO_BITS = 64_000;

function preferredMime() {
  const choices = ["video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  return choices.find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || "";
}

export const LivePhotoCaptureDialog = ({ open, onOpenChange, onCaptured, testId = "live-photo-capture" }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const restartTimerRef = useRef(null);
  const capturingRef = useRef(false);
  const segmentStartedAtRef = useRef(0);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [shotCount, setShotCount] = useState(0);
  const [error, setError] = useState("");

  // Keep a short rolling recording going in the background so that, the
  // moment the shutter is tapped, we already have a couple of seconds of
  // "before" motion (just like an iPhone Live Photo).
  const startSegment = () => {
    const stream = streamRef.current;
    if (!stream) return;
    let recorder;
    try {
      const mimeType = preferredMime();
      const opts = { videoBitsPerSecond: VIDEO_BITS, audioBitsPerSecond: AUDIO_BITS };
      if (mimeType) opts.mimeType = mimeType;
      recorder = new MediaRecorder(stream, opts);
    } catch {
      try { recorder = new MediaRecorder(stream); } catch { return; }
    }
    chunksRef.current = [];
    recorder.ondataavailable = (event) => { if (event.data?.size) chunksRef.current.push(event.data); };
    recorder.start(250);
    segmentStartedAtRef.current = performance.now();
    recorderRef.current = recorder;
    window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = window.setTimeout(() => {
      if (!capturingRef.current && recorderRef.current === recorder && recorder.state === "recording") {
        recorder.onstop = () => startSegment();
        recorder.stop();
      }
    }, IDLE_SEGMENT_MS);
  };

  const teardown = () => {
    window.clearTimeout(restartTimerRef.current);
    capturingRef.current = false;
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") { recorderRef.current.onstop = null; recorderRef.current.stop(); } } catch { /* noop */ }
    recorderRef.current = null;
    chunksRef.current = [];
    streamRef.current?.getTracks?.().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.pause(); videoRef.current.srcObject = null; } catch { /* noop */ } }
    setReady(false);
  };

  useEffect(() => {
    if (!open) { teardown(); return undefined; }
    let alive = true;
    setError("");
    setShotCount(0);
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        setError("Live capture is not supported by this browser. You can still upload a photo.");
        return;
      }
      try {
        // Video only — requesting the microphone was making the camera take
        // ~10s+ to start on the second capture (the mic stays busy from the
        // previous shot). Motion clips for tickets do not need audio.
        const video = { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: { ideal: "environment" } };
        const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
        if (!alive) { stream.getTracks().forEach((track) => track.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
        startSegment(); // begin buffering motion immediately
      } catch {
        setError("Camera access is unavailable. Check camera permission and try again.");
      }
    })();
    return () => { alive = false; teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const capture = async () => {
    const recorder = recorderRef.current;
    const video = videoRef.current;
    if (!recorder || !video || busy || !ready) return;
    capturingRef.current = true;
    window.clearTimeout(restartTimerRef.current);
    setBusy(true);
    try {
      // 1) Grab the still frame INSTANTLY (this is the "photo").
      const shutterAt = performance.now();
      const beforeShutterMs = Math.max(0, shutterAt - segmentStartedAtRef.current);
      setFlash(true);
      window.setTimeout(() => setFlash(false), 160);
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));

      // 2) Keep exactly as much motion after the shutter as this segment
      // already captured before it. The still is therefore the true midpoint.
      const stopped = new Promise((resolve) => { recorder.onstop = resolve; });
      const postProcessingMs = Math.max(0, performance.now() - shutterAt);
      await wait(Math.max(0, beforeShutterMs - postProcessingMs));
      if (recorder.state === "recording") recorder.stop();
      await stopped;

      const videoBlob = new Blob(chunksRef.current, { type: recorder.mimeType || "video/webm" });
      const stamp = Date.now();
      const extension = videoBlob.type.includes("mp4") ? "mp4" : "webm";
      const imageFile = new File([imageBlob], `LIVE_${stamp}.jpg`, { type: "image/jpeg" });
      const files = [imageFile];
      if (videoBlob.size > 0) files.push(new File([videoBlob], `LIVE_${stamp}.${extension}`, { type: videoBlob.type }));

      // Keep the camera warm and immediately ready for the next shot — this is
      // what removes the intermittent slow re-open/save when taking several.
      capturingRef.current = false;
      startSegment();
      setShotCount((n) => n + 1);

      // Save (compress + upload motion) in the BACKGROUND so it never blocks
      // taking the next photo.
      Promise.resolve(onCaptured(files)).catch((err) => toast.error(err?.message || "Could not save that photo."));
    } catch (captureError) {
      toast.error(captureError.message || "Could not capture the photo.");
      capturingRef.current = false;
      startSegment(); // resume buffering so the user can retry
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent closeTestId={`${testId}-close`} className="max-w-2xl p-0 overflow-hidden" data-testid={`${testId}-dialog`}>
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2"><Radio className="w-4 h-4 text-emerald-600" /> Live photo</DialogTitle>
          <DialogDescription>Tap to snap — each photo saves in the background, so keep shooting. Tap Done when you're finished.</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-[4/3] sm:aspect-video bg-black overflow-hidden">
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
          {ready && !error && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/55 rounded-full px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-white">Live</span>
            </div>
          )}
          {shotCount > 0 && (
            <div className="absolute top-3 right-3 bg-emerald-600 text-white text-[11px] font-bold rounded-full px-2.5 py-1" data-testid={`${testId}-count`}>
              {shotCount} added
            </div>
          )}
          {flash && <div className="absolute inset-0 bg-white/80" data-testid={`${testId}-flash`} />}
          {!ready && !error && <div className="absolute inset-0 grid place-items-center text-white"><Loader2 className="w-7 h-7 animate-spin" /></div>}
          {error && <div className="absolute inset-0 grid place-items-center p-8 text-center text-sm text-white bg-black" data-testid={`${testId}-error`}>{error}</div>}
        </div>
        <div className="px-5 pb-5 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testId}-cancel`}>
            {shotCount > 0 ? <>Done · {shotCount}</> : <><X className="w-4 h-4 mr-2" /> Cancel</>}
          </Button>
          <Button onClick={capture} disabled={!ready || busy || Boolean(error)} data-testid={`${testId}-shutter`}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}{busy ? "Snapping…" : (shotCount > 0 ? "Take another" : "Take photo")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
